import { useState, useCallback, useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux/store'
import { sendInvite, getRoomMembers } from '@/redux/asyncThunks/room'
import { fetchTeamsByRoomId } from '@/redux/asyncThunks/team'
import { createTeam } from '@/api/team'
import { UserRole } from '@/constants/room'
import { parseFile, RowData } from '@/utils/fileParsers'
import { validateRosterData, ValidationResult } from '@/utils/rosterValidation'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { FileUploadZone } from '@/components/InviteMembers/FileUploadZone'
import { FileInfoDisplay } from '@/components/InviteMembers/FileInfoDisplay'
import { LoadingIndicator } from '@/components/InviteMembers/LoadingIndicator'
import { RosterPreviewTable } from '@/components/InviteMembers/RosterPreviewTable'
import { ValidationSummary } from '@/components/InviteMembers/ValidationSummary'

interface UploadRosterModalProps {
  isOpen: boolean
  onClose: () => void
  roomId: string
  isGameStarted: boolean
}

export function UploadRosterModal({
  isOpen,
  onClose,
  roomId,
  isGameStarted,
}: UploadRosterModalProps) {
  const dispatch = useDispatch<AppDispatch>()
  const { roomMembers } = useSelector((state: RootState) => state.room)
  const { teams } = useSelector((state: RootState) => state.team)
  const [file, setFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<RowData[]>([])
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null)
  const [isParsing, setIsParsing] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0 || isGameStarted) return

      const selectedFile = acceptedFiles[0]
      setFile(selectedFile)
      setIsParsing(true)
      setValidationResult(null)
      setParsedData([])

      try {
        const data = await parseFile(selectedFile)
        setParsedData(data)

        setIsValidating(true)
        const result = validateRosterData(data, roomMembers, teams)
        setValidationResult(result)
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to parse file'
        )
        setFile(null)
        setParsedData([])
      } finally {
        setIsParsing(false)
        setIsValidating(false)
      }
    },
    [roomMembers, teams, roomId, isGameStarted]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
    },
    multiple: false,
    disabled: isGameStarted || isParsing || isValidating || isSending,
  })

  const handleRemoveFile = useCallback(() => {
    setFile(null)
    setParsedData([])
    setValidationResult(null)
  }, [])

  const handleSendInvites = useCallback(async () => {
    if (
      !validationResult ||
      !validationResult.isValid ||
      parsedData.length === 0
    ) {
      return
    }

    setIsSending(true)

    try {
      const finalTeamAssignments: Record<string, number> = {
        ...validationResult.teamMappings,
      }

      for (const teamName of validationResult.teamsToCreate) {
        try {
          const newTeam = await createTeam(roomId, { name: teamName })

          parsedData.forEach((row) => {
            const email = row.email.toLowerCase().trim()
            if (row.team.trim().toLowerCase() === teamName.toLowerCase()) {
              finalTeamAssignments[email] = newTeam.id
            }
          })
        } catch (error) {
          throw new Error(
            `Failed to create team "${teamName}": ${
              error instanceof Error ? error.message : 'Unknown error'
            }`
          )
        }
      }

      const emails = parsedData.map((row) => row.email.trim())

      await dispatch(
        sendInvite({
          roomId,
          emails,
          userRole: UserRole.Student,
          sendEmail: true,
          teamAssignments: finalTeamAssignments,
        })
      ).unwrap()

      dispatch(getRoomMembers(roomId))
      dispatch(fetchTeamsByRoomId(roomId))

      toast.success(`Successfully sent ${emails.length} invitation(s)`)
      handleRemoveFile()
      onClose()
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to send invitations. Please try again.'
      )
    } finally {
      setIsSending(false)
    }
  }, [validationResult, parsedData, roomId, dispatch, handleRemoveFile, onClose])

  const handleClose = useCallback(() => {
    if (!isSending) {
      handleRemoveFile()
      onClose()
    }
  }, [isSending, handleRemoveFile, onClose])

  const getRowStatus = useCallback(
    (rowIndex: number) => {
      if (!validationResult) return null
      const validationRowNum = rowIndex + 2
      const errors = validationResult.errors.filter(
        (e) => e.row === validationRowNum
      )
      const warnings = validationResult.warnings.filter(
        (w) => w.row === validationRowNum
      )
      return { errors, warnings, isValid: errors.length === 0 }
    },
    [validationResult]
  )

  const validRowCount = useMemo(() => {
    if (!validationResult) return 0
    return parsedData.filter((_, idx) => getRowStatus(idx)?.isValid).length
  }, [parsedData, validationResult, getRowStatus])

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden'>
        <DialogHeader className='flex-shrink-0'>
          <DialogTitle className='text-base font-bold text-[#0F172A]'>
            Upload Roster
          </DialogTitle>
          <DialogDescription className='m-0 text-sm text-[#64748B]'>
            Upload a CSV or Excel file with columns: firstname, lastname, email,
            team
          </DialogDescription>
        </DialogHeader>

        <div className='flex-1 min-h-0 overflow-y-auto pr-2'>
          <div className='flex flex-col gap-4'>
            {!file && (
              <FileUploadZone
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                isDragActive={isDragActive}
                disabled={isGameStarted}
              />
            )}

            {file && (
              <FileInfoDisplay
                fileName={file.name}
                rowCount={parsedData.length}
                onRemove={handleRemoveFile}
                isSending={isSending}
              />
            )}

            <LoadingIndicator
              isParsing={isParsing}
              isValidating={isValidating}
            />

            {parsedData.length > 0 && !isParsing && !isValidating && (
              <RosterPreviewTable
                parsedData={parsedData}
                validationResult={validationResult}
                getRowStatus={getRowStatus}
                validRowCount={validRowCount}
              />
            )}

            {validationResult && !isParsing && !isValidating && (
              <ValidationSummary
                validationResult={validationResult}
                parsedDataLength={parsedData.length}
              />
            )}
          </div>
        </div>

        <div className='flex justify-end gap-2 pt-4 border-t flex-shrink-0'>
          <Button
            type='button'
            variant='outline'
            onClick={handleClose}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={handleSendInvites}
            disabled={
              !validationResult?.isValid ||
              parsedData.length === 0 ||
              isSending ||
              isGameStarted
            }
            className='bg-dark-blue hover:bg-dark-blue-hovered'
          >
            {isSending ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Sending...
              </>
            ) : (
              `Send ${parsedData.length} Invitation(s)`
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
